import { useState, useEffect } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const TermsAcceptanceBanner = () => {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkTermsAcceptance();
  }, []);

  const checkTermsAcceptance = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Check localStorage for anonymous users
    const localAcceptance = localStorage.getItem("terms_accepted");
    
    if (user) {
      // Check database for authenticated users
      const { data } = await supabase
        .from("user_terms_acceptance")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (!data) {
        setOpen(true);
      }
    } else if (!localAcceptance) {
      // Show for anonymous users
      setOpen(true);
    }
  };

  const handleAccept = async () => {
    if (!accepted) {
      toast({
        title: "Please accept the terms",
        description: "You must check the box to accept our Terms & Conditions",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Save to database for authenticated users
        const { error } = await supabase
          .from("user_terms_acceptance")
          .insert({
            user_id: user.id,
            terms_version: "2025-01-06",
          });

        if (error) throw error;
      } else {
        // Save to localStorage for anonymous users
        localStorage.setItem("terms_accepted", "true");
      }

      toast({
        title: "Terms accepted",
        description: "Thank you for accepting our Terms & Conditions",
      });
      
      setOpen(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save acceptance",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl">Welcome to BitcoinMind.today</AlertDialogTitle>
          <AlertDialogDescription className="text-base space-y-4 pt-4">
            <p>
              Before you continue, please review and accept our Terms & Conditions. 
              This is required to access premium features and ensure you understand our policies.
            </p>
            
            <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
              <h4 className="font-semibold text-foreground">Key Points:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>All payments processed via cryptocurrency (OxaPay)</li>
                <li>Refund requests must be submitted within 48 hours</li>
                <li>Advertising packages are non-refundable</li>
                <li>Premium plans: from $9.99/month (yearly & lifetime options available)</li>
                <li>We use AI-generated content and analytics</li>
              </ul>
            </div>

            <p className="text-sm">
              By accepting, you agree to our{" "}
              <Link to="/terms-conditions" className="text-primary hover:underline font-semibold" target="_blank">
                Terms & Conditions
              </Link>,{" "}
              <Link to="/privacy-policy" className="text-primary hover:underline font-semibold" target="_blank">
                Privacy Policy
              </Link>, and{" "}
              <Link to="/refund-policy" className="text-primary hover:underline font-semibold" target="_blank">
                Refund Policy
              </Link>.
            </p>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox 
                id="terms" 
                checked={accepted}
                onCheckedChange={(checked) => setAccepted(checked as boolean)}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                I have read and accept the Terms & Conditions, Privacy Policy, and Refund Policy
              </label>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button 
            onClick={handleAccept} 
            disabled={!accepted || loading}
            className="w-full sm:w-auto"
          >
            {loading ? "Accepting..." : "Accept & Continue"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};